import { Temporal } from '@js-temporal/polyfill';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { crewQuery } from '../api/crew.ts';
import { venueInductionsQuery } from '../api/venue-page.ts';
import type { Venue } from '../api/venues.ts';
import { Button } from '../components/button.tsx';
import { Meter } from '../components/facts.tsx';
import { icons } from '../components/icons.ts';
import { RequiredTickets } from './crew-compliance.tsx';
import { VenueInstructions } from './instructions.tsx';
import { RequiredCovers } from './insurance.tsx';
import { dayName } from './roster-bars.tsx';
import { Renewal } from './venue-header.tsx';

// CB-REQ VEN-01 at v1.9104, the operator's Rules board of 2026-10-26 (V-5): the standing
// instructions (VEN-09) beside what crew need here — the induction and tickets (CMP-05) and the
// covers the venue asks to see (VEN-05) — and each person's induction for the venue as a bar of the
// time it has left, red within 30 days with *Record renewal*. The *Got it* counts are not drawn:
// nothing reads acknowledgements by instruction yet (as on the Overview).

const LAPSING_DAYS = 30;

/** "6 Oct 2027": a lapse up to a year out says its year. */
const withYear = (date: string) =>
  Temporal.PlainDate.from(date).toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

export function VenueRules({
  venue: v,
  capabilities,
  onChanged,
}: {
  venue: Venue;
  capabilities: readonly string[];
  onChanged: () => Promise<void>;
}) {
  const may = (key: string) => capabilities.includes(key);
  return (
    <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
      <VenueInstructions
        venueId={v.id}
        mayWrite={may('venues.instructions')}
        onChanged={onChanged}
      />
      <div className="flex flex-col gap-4">
        <section aria-labelledby="needs" className="panel flex flex-col gap-2 p-4">
          <h2 id="needs" className="panel-label">
            Required of crew
          </h2>
          <p className="flex items-center gap-2 text-sm">
            <icons.inductions size={16} aria-hidden className="text-accent-700" />
            <span className="flex-1">{v.induction ? v.induction.name : 'No induction'}</span>
            {v.induction ? (
              <span className="text-xs text-muted">valid {v.induction.validMonths} months</span>
            ) : null}
          </p>
          {may('crew.view') ? (
            <RequiredTickets
              owner="venues"
              id={v.id}
              label="Tickets everyone needs here"
              editable={may('crew.manage')}
            />
          ) : null}
        </section>
        <section aria-labelledby="covers" className="panel flex flex-col gap-2 p-4">
          <h2 id="covers" className="panel-label">
            Covers asked for
          </h2>
          <RequiredCovers venueId={v.id} editable={may('venues.manage')} />
        </section>
        {may('crew.view') && v.induction ? (
          <Inductions venue={v} mayRenew={may('crew.manage')} onChanged={onChanged} />
        ) : null}
      </div>
    </div>
  );
}

/** CMP-05: each person's latest induction here, the time left as a bar of its validity. */
function Inductions({
  venue: v,
  mayRenew,
  onChanged,
}: {
  venue: Venue;
  mayRenew: boolean;
  onChanged: () => Promise<void>;
}) {
  const inductions = useQuery(venueInductionsQuery(v.id));
  const crew = useQuery(crewQuery(false));
  const [renewing, setRenewing] = useState<{ crewMemberId: string; name: string } | null>(null);
  const today = Temporal.Now.plainDateISO();
  const validDays = (v.induction?.validMonths ?? 12) * 30.44;
  const nameOf = (id: string) => crew.data?.find((c) => c.id === id)?.displayName ?? 'Someone';
  const rows = [...(inductions.data ?? [])]
    .map((i) => ({
      ...i,
      left: today.until(Temporal.PlainDate.from(i.expiresOn), { largestUnit: 'day' }).days,
    }))
    .sort((a, b) => b.left - a.left);
  return (
    <section aria-labelledby="inductions" className="panel flex flex-col gap-2 p-4">
      {renewing ? (
        <Renewal
          venue={v}
          who={renewing}
          onDone={async () => {
            setRenewing(null);
            await onChanged();
          }}
          onClose={() => setRenewing(null)}
        />
      ) : null}
      <div className="flex items-center">
        <h2 id="inductions" className="panel-label flex-1">
          Inductions
        </h2>
        <span className="text-xs text-muted">Lapsing within a year</span>
      </div>
      {inductions.data && rows.length === 0 ? (
        <p className="text-sm text-muted">None recorded</p>
      ) : null}
      <ul className="flex flex-col gap-1">
        {rows.map((i) => {
          const lapsing = i.left <= LAPSING_DAYS;
          const name = nameOf(i.crewMemberId);
          return (
            <li key={i.id} className="flex flex-col gap-1">
              <Meter
                label={lapsing ? <strong>{name}</strong> : name}
                fraction={Math.max(0.01, i.left / validDays)}
                signal={lapsing}
                figure={
                  lapsing ? (
                    <span className="tag tag-accent-2">{dayName(i.expiresOn)}</span>
                  ) : (
                    <span className="text-xs text-muted">to {withYear(i.expiresOn)}</span>
                  )
                }
              />
              {lapsing && mayRenew ? (
                <Button
                  variant="quiet"
                  className="small-btn self-end"
                  onClick={() => setRenewing({ crewMemberId: i.crewMemberId, name })}
                >
                  Record renewal<span className="sr-only"> for {name}</span>
                </Button>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
